import { Box, Card, CardContent, CardHeader, Link, Stack, Typography, useTheme } from "@mui/material";
import { AccountProfileV2FormType } from "./AccountProfileV2Utils";
import { useFieldArray, useFormContext } from "react-hook-form";
import NoData from "../no-data/NoData";
import UserRecipientList from "../user-recipient-list/UserRecipientList";
import { Mail, Phone, Sms } from "@mui/icons-material";
import TwoStepConfirmationDialog, { bindTwoStepConfirmationDialog, useTwoStepConfirmationDialogState } from "../two-step-confirmation-dialog/TwoStepConfirmationDialog";
import { useTranslation } from "react-i18next";
import Enumerable from "linq";
import { RecipientTypeEnum } from "../../common/enums/RecipientTypeEnum";
import { UserRecipientFieldArrayType } from "../user-recipient-list/UserRecipientFieldTypes";
import { getRecipientTypeName } from "../../common/utilities/GetRecipientTypeName";


export default function UserNotificationsCard() {

    const {t} = useTranslation();
    const theme = useTheme();

    const disableConfirmationDialog = useTwoStepConfirmationDialogState();

    const {
            watch,
            //setValue,
        } = useFormContext<AccountProfileV2FormType>();    
    
    
        const [
            //selectedSource,
            //email, 
            //authenticationGuid,
            watchRecipients
         ] = watch([
            //"mfaSource",
            //'email',
            //"authenticationGuid",
            'recipients'
        ]);


    const {
        fields,
        //append,
        //remove,
        update
    } = useFieldArray<AccountProfileV2FormType, "recipients", "fieldID">({ name: "recipients", keyName: "fieldID" });


    const recipients: UserRecipientFieldArrayType =
        fields.map((field, index) => {
            return {
                ...field,
                ...watchRecipients[index],
                recipientKey: `recipients.${index}`
            };
        });

    const emailRecipients = Enumerable
        .from(recipients)
        .where(r => r.type === RecipientTypeEnum.Email)
        .orderBy(a => a.to)
        .toArray();
    const smsRecipients = Enumerable
        .from(recipients)
        .where(r => r.type === RecipientTypeEnum.Text)
        .orderBy(a => a.to)
        .toArray();
    const voiceRecipients = Enumerable
        .from(recipients)
        .where(r => r.type === RecipientTypeEnum.Voice)
        .orderBy(a => a.to)
        .toArray();

    async function onDisableRecipient(recipientKey: string, disable: boolean) {

        const index = recipients.findIndex(r => r.recipientKey === recipientKey);

        if (index > -1) {

            const recipient = fields[index];

            if (disable && !recipient.disabledTimestamp) {

                const recipientTypeName = getRecipientTypeName(recipient.type);

                const result = await disableConfirmationDialog.open({
                    title: t("userEditor.steps.alarmNotifications.disableConfirmation.title"),
                    explanation: (
                        <Stack direction="column" spacing={2}>
                            <Typography>
                                {t("userEditor.steps.alarmNotifications.disableConfirmation.explanation1", { recipientType: recipientTypeName, recipientTo: recipient.to })}
                            </Typography>
                            <Typography sx={{
                                fontWeight: "bold"
                            }}>
                                {t("userEditor.steps.alarmNotifications.disableConfirmation.explanation2", { recipientType: recipientTypeName, recipientTo: recipient.to })}
                            </Typography>
                        </ Stack>
                    ),
                    confirmationText: t("userEditor.steps.alarmNotifications.disableConfirmation.confirmationText", { recipientType: recipientTypeName })
                });

                if (result.reason === "ok") {
                    update(index, {
                        ...recipient,
                        disabled: disable
                    });
                }
            } else {
                update(index, {
                    ...recipient,
                    disabled: disable
                });
            }
        }
    }


    return (
        <>
            <Card
                raised={true}
                sx={{
                    flex: "0 0 auto",
                    margin: theme.spacing(1),
                    padding: theme.spacing(2),
                    height: '100%'
                }}
            >
                <CardHeader
                    sx={{
                        padding: 1
                    }}
                    //title={userState.user?.displayText}
                    title={t('profile.notifications')}
                    slotProps={{
                        title: { 
                            sx: {
                                fontWeight: "bold", 
                            },
                            variant: 'subtitle1', 
                                //marginLeft: 2 
                        }
                    }}
                />
                
                <CardContent
                    sx={{
                        padding: 1,
                        paddingBottom: theme => `${theme.spacing(1)} !important`
                    }}
                >

                {recipients.length === 0 &&
                        <NoData
                            title={t("userEditor.steps.alarmNotifications.noRecipients.title")}
                            text={t("userEditor.steps.alarmNotifications.noRecipients.text")}
                        />
                    }

                    {emailRecipients.length > 0 &&
                        <UserRecipientList
                            title={t("userEditor.steps.alarmNotifications.emailNotifications")}
                            icon={<Mail />}
                            recipients={emailRecipients}
                            onDisable={onDisableRecipient}
                            flat={true}
                            //hideUsage={true}
                        />
                    }

                    {smsRecipients.length > 0 &&
                        <UserRecipientList
                            title={t("userEditor.steps.alarmNotifications.smsNotifications")}
                            icon={<Sms />}
                            recipients={smsRecipients}
                            onDisable={onDisableRecipient}
                            flat={true}
                            //hideUsage={true}
                        />
                    }

                    {voiceRecipients.length > 0 &&
                        <UserRecipientList
                            title={t("userEditor.steps.alarmNotifications.voiceNotifications")}
                            icon={<Phone />}
                            recipients={voiceRecipients}
                            onDisable={onDisableRecipient}
                            flat={true}
                            //hideUsage={true}
                        />
                    }

                    {smsRecipients.length > 0 &&
                    <Stack direction='column' sx={{
                        marginTop: 2
                    }}>

                        <Typography variant='body2'>
                            You have been setup to receive alerts from eLynx Technologies. SMS Msg freq may vary. Msg & data rates may apply. Reply STOP to end all messages. Reply HELP to view our help menu.
                        </Typography>

                        <Box sx={{ 
                            whiteSpace: 'normal', 
                            fontSize: '.875rem', 
                            marginTop: 1
                            
                            }}
                        >
                            <Link aria-label="terms and conditions" color="textPrimary" href="https://www.elynxtech.com/terms-and-conditions" target="_blank" variant='inherit' sx={{
                                fontWeight: 600
                            }}>
                                Terms and Conditions&nbsp;
                            </Link>
                            and&nbsp;
                            <Link aria-label="privacy policy" color="textPrimary" href="https://www.iubenda.com/privacy-policy/70098978" target="_blank" variant='inherit' sx={{
                                fontWeight: 600
                            }}>
                                Privacy Policy
                            </Link>
                        </Box>
                        <Typography  variant='body2' sx={{
                            marginTop: 1
                        }}>
                            If you need further assistance, please visit support.elynxtech.com
                        </Typography>
                    </Stack>
                    }

                </CardContent>
            </Card>
            <TwoStepConfirmationDialog {...bindTwoStepConfirmationDialog(disableConfirmationDialog)} />
        </>
    );
}